import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { hasLastSavedMetadata, normalizeLastSavedName } from "../../../utils/last_saved_metadata.util";
import { NavigationMenuSelection } from "../../../../../interfaces/navigation_menu.interface";
import { RequirementFollowUpLink } from "../../../utils/follow_up_links";

@Component({
    selector: "tr[appSaqResultsDescriptionFooter]",
    templateUrl: "./results_description.component.html",
    styleUrls: ["./results_description.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultsDescriptionFooterComponent {
    @Input()
    resultsDescription = "";

    @Input()
    isSaving = false;

    @Input()
    isDisabled = false;

    @Input()
    readOnlyUser = false;

    @Input()
    validationError: string | null = null;

    @Input()
    lastSavedAt: string | null = null;

    @Input()
    lastSavedByName: string | null = null;

    @Input()
    followUpLink: RequirementFollowUpLink | null = null;

    @Input()
    isFormComplete = false;

    @Input()
    isSaveDisabled = false;

    @Input()
    isDescriptionRequired = true;

    @Input()
    activeComplianceStatus: 0 | 1 | null = null;

    @Input()
    hasAssignedQa = false;

    @Input()
    isCurrentUserAssignedQa = false;

    @Input()
    qsaReviewedInput: 0 | 1 = 0;

    @Input()
    isMarkingNeedsReview = false;

    @Input()
    isNeedsReviewDisabled = false;

    @Output()
    readonly resultsDescriptionChange = new EventEmitter<string>();

    @Output()
    readonly saveRequested = new EventEmitter<void>();

    @Output()
    readonly saveDraftRequested = new EventEmitter<void>();

    @Output()
    readonly followUpLinkActivated = new EventEmitter<NavigationMenuSelection>();

    @Output()
    readonly needsReviewRequested = new EventEmitter<void>();

    get hasLastSavedInfo(): boolean {
        return hasLastSavedMetadata(this.lastSavedAt, this.lastSavedByName);
    }

    get hasValidationError(): boolean {
        return typeof this.validationError === "string" && this.validationError.trim().length > 0;
    }

    get isInteractionDisabled(): boolean {
        return this.isDisabled || this.readOnlyUser;
    }

    get shouldShowNeedsReviewAction(): boolean {
        return this.activeComplianceStatus === 1 ||
            (this.hasAssignedQa && this.qsaReviewedInput === 1 && this.activeComplianceStatus === 0);
    }

    get shouldShowSaveAndMarkReviewedAction(): boolean {
        return this.activeComplianceStatus !== 1 && !this.isAssessorReviewedByAnotherUser;
    }

    private get isAssessorReviewedByAnotherUser(): boolean {
        return this.hasAssignedQa && !this.isCurrentUserAssignedQa && this.qsaReviewedInput === 1;
    }

    get lastSavedDisplayName(): string | null {
        return normalizeLastSavedName(this.lastSavedByName);
    }

    onDescriptionInput(value: string): void {
        if (this.isInteractionDisabled) {
            return;
        }

        this.resultsDescriptionChange.emit(value);
    }

    onSaveClicked(): void {
        if (
            this.isSaving ||
            this.isFormComplete ||
            this.isSaveDisabled ||
            this.isInteractionDisabled
        ) {
            return;
        }

        this.saveRequested.emit();
    }

    onSaveDraftClicked(): void {
        if (
            this.isSaving ||
            this.isFormComplete ||
            this.isSaveDisabled ||
            this.isInteractionDisabled
        ) {
            return;
        }

        this.saveDraftRequested.emit();
    }

    onFollowUpLinkClicked(event: MouseEvent): void {
        event.preventDefault();

        if (!this.followUpLink || this.isInteractionDisabled) {
            return;
        }

        this.followUpLinkActivated.emit(this.followUpLink.targetSelection);
    }

    onNeedsReviewClicked(): void {
        if (this.isInteractionDisabled || this.isFormComplete || this.isNeedsReviewDisabled) {
            return;
        }

        this.needsReviewRequested.emit();
    }
}
